import CloseIcon from '@mui/icons-material/Close';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useContext, useEffect, useRef, useState } from 'react';

import { Context } from '../../service/Context';
import VirtualizedListbox from '../../utils/UtilesBuscadores';
import { capitalizar } from '../../utils/UtilesComun';
import { BadgeInfoApp } from '../custom/CustomComponents';
import CrearJugadorModal from '../jugador/CrearJugadorModal';

const ModalAnadirJugadorLista = ({ open, onClose, onAddJugador, nombreLista }) => {
  const modalCrearRef = useRef(null);
  const inputRef = useRef(null);
  const { idUsuario, todosJugadores, obtenerTodosJugadores } = useContext(Context);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (open && idUsuario && (!todosJugadores || todosJugadores.length === 0)) {
      obtenerTodosJugadores();
    }
  }, [open, idUsuario, todosJugadores, obtenerTodosJugadores]);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 150);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [open]);

  const handleCloseModal = () => {
    setInputValue('');
    onClose();
  };

  const normalizeStr = (str) =>
    (str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const customFilterOptions = (options, { inputValue: searchVal }) => {
    if (!searchVal) {
      return options;
    }

    const inputTerms = normalizeStr(searchVal)
      .split(' ')
      .filter((term) => term.length > 0);

    return options.filter((option) => {
      const normalizedLabel = normalizeStr(option.nombreJugador);
      return inputTerms.every((term) => normalizedLabel.includes(term));
    });
  };

  const handleSelectJugador = (event, jugador) => {
    if (jugador) {
      onAddJugador(jugador);
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 1.5,
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
            border: '1px solid #e2e8f0',
          },
        }}
      >
        {/* Cabecera del Modal */}
        <DialogTitle sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '10px',
                backgroundColor: '#f0fdfa',
                border: '1px solid #99f6e4',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0f766e',
              }}
            >
              <PersonAddAlt1Icon sx={{ fontSize: '1.25rem' }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#0f172a' }}>
                Añadir jugador a la lista
              </Typography>
              {nombreLista && (
                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', fontWeight: 500 }}>
                  Lista destino: <strong style={{ color: '#0f766e' }}>{nombreLista}</strong>
                </Typography>
              )}
            </Box>
          </Box>

          <IconButton onClick={onClose} size="small" sx={{ color: '#94a3b8', '&:hover': { color: '#0f172a' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        {/* Contenido del Modal con Buscador a Ancho Completo */}
        <DialogContent sx={{ p: 1.5, overflow: 'visible' }}>
          <Typography sx={{ fontSize: '0.82rem', color: '#475569', mb: 1.5, fontWeight: 500 }}>
            Busca el jugador por nombre o apellidos para incorporarlo inmediatamente:
          </Typography>

          <Box sx={{ width: '100%', mb: 2 }}>
            <Autocomplete
              id="modal-buscador-jugadores"
              fullWidth
              filterOptions={customFilterOptions}
              options={todosJugadores || []}
              ListboxComponent={VirtualizedListbox}
              getOptionLabel={(option) => capitalizar(option.nombreJugador || '')}
              onChange={handleSelectJugador}
              inputValue={inputValue}
              onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
              clearOnBlur={false}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={inputRef}
                  placeholder="Escribe el nombre del jugador..."
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: <SearchIcon sx={{ color: '#0f766e', fontSize: '1.2rem', ml: 0.5, mr: 0.5 }} />,
                    sx: {
                      height: 42,
                      fontSize: '0.88rem',
                      borderRadius: '10px',
                      backgroundColor: '#f8fafc',
                      '& fieldset': {
                        borderColor: '#cbd5e1',
                      },
                      '&:hover fieldset': {
                        borderColor: '#0f766e',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#0f766e',
                        borderWidth: 2,
                      },
                    },
                  }}
                  inputProps={{
                    ...params.inputProps,
                    onKeyDown: (e) => {
                      if (e.key === 'Escape') {
                        e.stopPropagation();
                        handleCloseModal();
                      }
                      params.inputProps?.onKeyDown?.(e);
                    },
                  }}
                />
              )}
              renderOption={(props, option) => [
                props,
                <Box
                  key={option.idJugador}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    py: 0.25,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BadgeInfoApp>{option.ano || '?'}</BadgeInfoApp>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#0f172a' }}>
                      {capitalizar(option.nombreJugador)}
                    </Typography>
                  </Box>
                  {option.nombreEquipo && (
                    <Typography sx={{ fontSize: '0.76rem', color: '#64748b' }}>
                      {capitalizar(option.nombreEquipo)}
                    </Typography>
                  )}
                </Box>,
              ]}
              PaperComponent={(paperProps) => (
                <Paper
                  {...paperProps}
                  sx={{
                    mt: 1,
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1',
                    boxShadow: '0 12px 28px rgba(0, 0, 0, 0.12)',
                    overflow: 'hidden',
                  }}
                />
              )}
              componentsProps={{
                popper: {
                  style: { width: '100%', minWidth: 350, zIndex: 1400 },
                },
              }}
            />
          </Box>

          {/* Sección para crear jugador si no existe */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1.5,
              borderRadius: '10px',
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
            }}
          >
            <Box>
              <Typography sx={{ fontSize: '0.80rem', fontWeight: 600, color: '#1e293b' }}>
                ¿No encuentras al jugador?
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                Puedes darlo de alta manualmente ahora mismo
              </Typography>
            </Box>

            <Button
              size="small"
              onClick={() => modalCrearRef.current?.handleOpen()}
              sx={{
                height: 32,
                px: 1.75,
                borderRadius: '16px',
                backgroundColor: '#0f766e',
                color: '#ffffff',
                fontSize: '0.76rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 1px 2px rgba(15, 118, 110, 0.2)',
                '&:hover': {
                  backgroundColor: '#0d9488',
                  boxShadow: '0 2px 4px rgba(15, 118, 110, 0.3)',
                },
              }}
            >
              + Crear jugador
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <CrearJugadorModal ref={modalCrearRef} />
    </>
  );
};

ModalAnadirJugadorLista.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAddJugador: PropTypes.func.isRequired,
  nombreLista: PropTypes.string,
};

export default ModalAnadirJugadorLista;
